import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Add, ExpandMore, SpaceDashboardOutlined } from "@mui/icons-material"
import { Box, Divider, IconButton, Skeleton, Tabs, Tooltip, Typography, styled, type SxProps, type Theme } from "@mui/material"
import { blue } from "@mui/material/colors"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { OK, ERROR } from "helpers/data-statuses"
import { useAppDispatch } from "hooks/redux"
import { useAccountAlias, useIsMultiDashboardsEnabled } from "resources/account"
import {
  useDashboardInstances,
  type DashboardInstance,
} from "resources/dashboard-instances"
import { compareByRecency } from "../../store/instances-slice"
import { useDashboardId } from "../../context/dashboard-id"
import { useArrangementGetter } from "../../hooks/use-arrangement-getter"
import { useDuplicateDashboard } from "../../hooks/use-duplicate-dashboard"
import { useRenameDashboard } from "../../hooks/use-rename-dashboard"
import { useDeleteDashboard } from "../../hooks/use-delete-dashboard"
import DashboardTab from "./dashboard-tab"
import DeleteDashboardDialog from "./delete-dashboard-dialog"
import NewDashboardDialog from "./new-dashboard-dialog"
import RenameDashboardDialog from "./rename-dashboard-dialog"
import LayoutActionsMenu from "./layout-actions-menu"
import SwitcherOverflowMenu from "./switcher-overflow-menu"
import TabActionsMenu from "./tab-actions-menu"

// Stable empty fallback: a fresh [] each render would re-run the sort memo and churn the Tabs.
const EMPTY_INSTANCES: DashboardInstance[] = []

// Selection is driven by DashboardTab's onClick → navigate → URL, not MUI's onChange — but a
// controlled Tabs still requires the prop, or MUI logs a dev-only console error. [APP-3266]
const NO_OP_TAB_CHANGE = (): void => { /* selection navigates via DashboardTab's onClick */ }

// Embeds in the dashboard header (no own background/border). flex:1 fills the old icon/title space;
// minWidth:0 lets the scrollable strip shrink.
const SWITCHER_ROW_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  flex: 1,
  minWidth: 0,
  alignSelf: "stretch",
} satisfies SxProps<Theme>

// Loading skeletons sit a touch further apart than the live tab row.
const LOADING_ROW_SX = { ...SWITCHER_ROW_SX, gap: 1 } satisfies SxProps<Theme>

// Vertical separators between the cluster's zones (switch | create/switch | manage).
const DIVIDER_SX = { mx: 0.5, my: 1 } satisfies SxProps<Theme>

// AGENTS.md's "never hard-code a hex a theme token covers" doesn't apply here: theme.palette.primary
// shifts by mode (#03a9f4 light / #0277bd dark), and this indicator is deliberately mode-INVARIANT —
// no existing token covers a value that must stay put across both. Named (not inlined) so a future
// second use doesn't re-derive it. [APP-3271/3309]
const TAB_INDICATOR_COLOR = blue[500]

// Flat tab row carried by a top indicator bar (no track/pill). A styled component (not a shared sx)
// so the style injects once. [APP-3271]
const DashboardTabs = styled(Tabs)(({ theme }) => ({
  // Fill the header height (override MUI's 48px min) and stretch so the top bar sits flush with the top.
  height: "100%",
  minHeight: 0,
  flex: "0 1 auto",
  maxWidth: "100%",
  minWidth: 0,
  alignSelf: "stretch",
  // v9 renamed the flex container class flexContainer -> list; height:100% here is what
  // vertically centers the tabs and fills the 50px strip (else the list collapses to content height).
  "& .MuiTabs-list": { gap: theme.spacing(2), height: "100%" },
  "& .MuiTabs-indicator": {
    // Top bar (not MUI's default bottom), 3px for legibility.
    top: 0,
    bottom: "unset",
    height: 3,
    borderRadius: "0 0 2px 2px",
    backgroundColor: TAB_INDICATOR_COLOR,
    transition: theme.transitions.create(["left", "width"], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
}))

export default function DashboardSwitcher() {
  const isEnabled = useIsMultiDashboardsEnabled()
  const [instances, status] = useDashboardInstances(isEnabled)
  const currentId = useDashboardId()
  const accountAlias = useAccountAlias()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const getArrangement = useArrangementGetter()
  const { duplicate, isDuplicating } = useDuplicateDashboard(currentId, getArrangement)
  const { rename } = useRenameDashboard()
  const { remove } = useDeleteDashboard()

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null)
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false)
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  // Fires the load-failure toast at most once per mount; a boolean ref (not state) avoids a
  // re-render and keeps the dispatch in the effect, not the render body.
  const erroredToastedRef = useRef(false)

  const sortedInstances = useMemo(
    () => [...(instances ?? EMPTY_INSTANCES)].sort(compareByRecency),
    [instances],
  )

  // A resource gate must accept ERROR as terminal too, or the bar hangs on the skeleton forever
  // when the fetch errors. On ERROR we render just the "+ New" affordance (and toast once).
  const isLoading = !(status === OK || status === ERROR)

  useEffect(() => {
    if (status !== ERROR || erroredToastedRef.current) return
    erroredToastedRef.current = true
    dispatch(NotificationCreators.add("error", null, "Could not load your dashboards."))
  }, [status, dispatch])

  const handleSelect = useCallback(
    (slug: string) => {
      if (!accountAlias) return
      navigate(`/${accountAlias}/dashboard/${slug}`)
    },
    [accountAlias, navigate],
  )

  const closeMenu = useCallback(() => { setMenuAnchor(null) }, [])

  const openActions = useCallback((anchorEl: HTMLElement) => { setActionsAnchor(anchorEl) }, [])
  const closeActions = useCallback(() => { setActionsAnchor(null) }, [])

  // Both the "+ New" button and the overflow item open the create dialog.
  const openNewDialog = useCallback(() => { setIsNewDialogOpen(true) }, [])
  const closeNewDialog = useCallback(() => { setIsNewDialogOpen(false) }, [])

  const openRenameDialog = useCallback(() => { setIsRenameDialogOpen(true) }, [])
  const closeRenameDialog = useCallback(() => { setIsRenameDialogOpen(false) }, [])
  const openDeleteDialog = useCallback(() => { setIsDeleteDialogOpen(true) }, [])
  const closeDeleteDialog = useCallback(() => { setIsDeleteDialogOpen(false) }, [])

  if (!isEnabled) {
    // Flag off: the original single-dashboard chrome — a big dashboard glyph + "Dashboard" label.
    // The switcher tab strip only appears once multi-dashboard is enabled.
    return (
      <Box sx={SWITCHER_ROW_SX}>
        <SpaceDashboardOutlined sx={{ fontSize: 30, color: "common.onBackground" }} />
        <Typography sx={{ fontSize: "18px", lineHeight: 1, color: "common.onBackground", whiteSpace: "nowrap" }}>
          Dashboard
        </Typography>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box sx={LOADING_ROW_SX}>
        <Skeleton variant="rounded" width={132} height={26} />
        <Skeleton variant="rounded" width={132} height={26} />
      </Box>
    )
  }

  // Match the active tab on instanceId or slug (URL may carry either); false on the bare /dashboard
  // landing avoids MUI's out-of-range warning.
  const activeInstance = sortedInstances.find(
    (instance) => instance.instanceId === currentId || instance.slug === currentId,
  )
  const activeValue = activeInstance ? activeInstance.slug : false

  return (
    <Box sx={SWITCHER_ROW_SX}>
      {sortedInstances.length > 0 && (
        <DashboardTabs value={activeValue} onChange={NO_OP_TAB_CHANGE} variant="scrollable" scrollButtons="auto">
          {sortedInstances.map((instance) => (
            <DashboardTab
              key={instance.instanceId}
              value={instance.slug}
              name={instance.name}
              onSelect={handleSelect}
              onActions={openActions}
            />
          ))}
        </DashboardTabs>
      )}
      {/* Divider + overflow switcher — gated together so the empty state isn't a stray divider
          and a "More dashboards" menu listing only "New". */}
      {sortedInstances.length > 0 && (
        <>
          <Divider orientation="vertical" flexItem sx={DIVIDER_SX} />
          <Tooltip title="More dashboards">
            <IconButton
              aria-label="More dashboards"
              size="small"
              onClick={(event) => { setMenuAnchor(event.currentTarget) }}
            >
              <ExpandMore />
            </IconButton>
          </Tooltip>
        </>
      )}
      <Tooltip title="New dashboard">
        <IconButton
          aria-label="New dashboard"
          size="small"
          onClick={openNewDialog}
        >
          <Add />
        </IconButton>
      </Tooltip>
      {/* Set the layout-management menu apart from the create/switch controls — different intent. */}
      <Divider orientation="vertical" flexItem sx={DIVIDER_SX} />
      <LayoutActionsMenu />
      <SwitcherOverflowMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        instances={sortedInstances}
        currentId={currentId}
        onSelect={handleSelect}
        onNewDashboard={openNewDialog}
      />
      <TabActionsMenu
        anchorEl={actionsAnchor}
        open={Boolean(actionsAnchor) && Boolean(activeInstance)}
        onClose={closeActions}
        onDuplicate={() => {
          // The dashboard (instance) name is the "(copy)" base — duplicate clones the instance, not its layout.
          if (activeInstance) duplicate(activeInstance.layoutId, activeInstance.name)
        }}
        isDuplicating={isDuplicating}
        onRename={openRenameDialog}
        onDelete={openDeleteDialog}
      />
      <NewDashboardDialog open={isNewDialogOpen} onClose={closeNewDialog} />
      {activeInstance && isRenameDialogOpen && (
        <RenameDashboardDialog
          open
          currentName={activeInstance.name}
          onClose={closeRenameDialog}
          onRename={(name) => rename(activeInstance.instanceId, name)}
        />
      )}
      {activeInstance && isDeleteDialogOpen && (
        <DeleteDashboardDialog
          open
          dashboardName={activeInstance.name}
          onClose={closeDeleteDialog}
          onDelete={() => remove(activeInstance.instanceId)}
        />
      )}
    </Box>
  )
}
